import React, { useEffect, useRef } from "react";
import AskOpusOrbIcon from "@src/features/assistantChat/AskOpusOrbIcon";
import {
  AGENT_COLORS,
  BotOrb,
  type AgentColorSlug,
  useHoverSpin,
} from "./AgentAvatar";

// Two tilted orbits of agent bots around Ask Opus, turning in opposite
// directions. Sizes are fractions of the header so the rings scale with the
// dialog; `speed` is revolutions per second.
const ORBITS = [
  { rx: 0.44, ry: 0.3, speed: 1 / 48, bots: 4, phase: 0.3 },
  { rx: 0.27, ry: 0.19, speed: -1 / 36, bots: 3, phase: 1.1 },
] as const;
const CENTER = { x: 0.52, y: 0.4 };

const CHIP_PX = 44;

// Demo updates a bot "says" now and then: the kinds of jobs agents do for a
// restaurant training team, first person, short enough for one line.
const BUBBLE_MESSAGES = [
  "I've created a Closing Procedures course",
  "I've assigned Food Safety to FOH",
  "3 alcohol certs expire this week",
  "I've nudged 6 servers who are overdue",
  "Onboarding is ready for 4 new hires",
  "I've translated 2 modules to Spanish",
  "Opening checklists are up 12%",
  "I've drafted a pre-shift check-in",
  "I've flagged 5 modules nobody opens",
  "Riverside is fully compliant",
];
// A bubble pops every BUBBLE_EVERY seconds and stays up for BUBBLE_FOR.
const BUBBLE_EVERY = 3.2;
const BUBBLE_FOR = 2.6;
const BUBBLE_FADE = 0.25;

type Props = {
  children: React.ReactNode;
};

// One bot in its white chip, riding the ring (placed by the header's loop
// through chipRef). Hovered, it spins once.
const OrbitChip: React.FC<{
  color: AgentColorSlug;
  chipRef: (el: HTMLDivElement | null) => void;
}> = ({ color, chipRef }) => {
  const greet = useHoverSpin();
  return (
    <div
      ref={chipRef}
      className="absolute top-0 left-0 flex items-center justify-center rounded-full bg-bg-primary shadow-default will-change-transform"
      style={{ width: CHIP_PX, height: CHIP_PX }}
      aria-hidden="true"
      onMouseEnter={greet.onEnter}
      onMouseLeave={greet.onLeave}
    >
      <BotOrb color={color} size={30} state={greet.state} />
    </div>
  );
};

/**
 * An illustrated dialog header: agent bots in white chips circling the Ask
 * Opus orb, with the dialog's title block laid over the bottom. Decorative;
 * holds still under reduced motion.
 */
const AgentOrbitHeader: React.FC<Props> = ({ children }) => {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const chipRefs = useRef<(HTMLDivElement | null)[]>([]);
  const ringRefs = useRef<(SVGEllipseElement | null)[]>([]);
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const bubbleTextRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf = 0;
    const start = performance.now();
    // Which chip is speaking, and since when; -1 between bubbles.
    const bubble = { chip: -1, since: 0, message: 0 };
    const chipPos: { x: number; y: number }[] = [];

    const place = (now: number) => {
      const w = stage.clientWidth;
      const h = stage.clientHeight;
      const cx = w * CENTER.x;
      const cy = h * CENTER.y;
      const t = (now - start) / 1000;
      let i = 0;
      ORBITS.forEach((orbit, o) => {
        const rx = w * orbit.rx;
        const ry = h * orbit.ry;
        const ring = ringRefs.current[o];
        if (ring) {
          ring.setAttribute("cx", String(cx));
          ring.setAttribute("cy", String(cy));
          ring.setAttribute("rx", String(rx));
          ring.setAttribute("ry", String(ry));
        }
        for (let b = 0; b < orbit.bots; b++, i++) {
          const chip = chipRefs.current[i];
          if (!chip) continue;
          const angle =
            orbit.phase +
            (b / orbit.bots) * Math.PI * 2 +
            (reduced ? 0 : t * orbit.speed * Math.PI * 2);
          const x = cx + Math.cos(angle) * rx;
          const y = cy + Math.sin(angle) * ry;
          // 0 at the back of the ring, 1 at the front.
          const depth = (Math.sin(angle) + 1) / 2;
          chip.style.transform = `translate(${x - CHIP_PX / 2}px, ${
            y - CHIP_PX / 2
          }px) scale(${0.72 + depth * 0.34})`;
          chip.style.opacity = String(0.45 + depth * 0.55);
          chip.style.zIndex = String(depth > 0.5 ? 3 : 1);
          chipPos[i] = { x, y };
        }
      });
      if (!reduced) placeBubble(t, w, h);
      if (!reduced) raf = requestAnimationFrame(place);
    };

    // Speech bubbles: every few seconds a bot in the upper band (so the
    // bubble clears the top edge and the title) says one demo update, and
    // the bubble rides along with it until it fades.
    const placeBubble = (t: number, w: number, h: number) => {
      const el = bubbleRef.current;
      const text = bubbleTextRef.current;
      if (!el || !text) return;
      const slot = Math.floor(t / BUBBLE_EVERY);
      const into = t - slot * BUBBLE_EVERY;
      // A new cycle picks the next speaker (the first comes one cycle in).
      if (bubble.since !== slot) {
        const candidates = chipPos
          .map((p, i) => ({ ...p, i }))
          .filter((p) => p && p.y > h * 0.26 && p.y < h * 0.58);
        const pick = candidates[slot % Math.max(candidates.length, 1)];
        bubble.chip = pick ? pick.i : -1;
        bubble.since = slot;
        bubble.message = (bubble.message + 1) % BUBBLE_MESSAGES.length;
        text.textContent = BUBBLE_MESSAGES[bubble.message];
      }
      const pos = bubble.chip >= 0 ? chipPos[bubble.chip] : null;
      const visible = pos !== null && into < BUBBLE_FOR;
      const fade = Math.min(
        into / BUBBLE_FADE,
        (BUBBLE_FOR - into) / BUBBLE_FADE,
        1,
      );
      el.style.opacity = visible ? String(Math.max(fade, 0)) : "0";
      if (!pos) return;
      // Centered over the chip, kept inside the header's sides.
      const half = el.offsetWidth / 2;
      const left = Math.min(Math.max(pos.x - half, 8), w - half * 2 - 8);
      const rise = visible ? (1 - Math.min(into / BUBBLE_FADE, 1)) * 6 : 0;
      el.style.transform = `translate(${left}px, ${
        pos.y - CHIP_PX / 2 - el.offsetHeight - 6 + rise
      }px)`;
      el.style.setProperty("--tail-x", `${pos.x - left}px`);
    };
    raf = requestAnimationFrame(place);
    return () => cancelAnimationFrame(raf);
  }, []);

  let chipIndex = 0;
  return (
    <div
      ref={stageRef}
      className="relative h-[216px] shrink-0 overflow-hidden bg-bg-secondary"
      data-testid="agent-orbit-header"
    >
      <svg
        className="pointer-events-none absolute inset-0 size-full"
        aria-hidden="true"
      >
        {ORBITS.map((_, o) => (
          <ellipse
            key={o}
            ref={(el) => {
              ringRefs.current[o] = el;
            }}
            fill="none"
            className="stroke-border-primary"
            strokeWidth={1.5}
          />
        ))}
      </svg>
      <div
        className="absolute z-[2] flex size-14 items-center justify-center rounded-full bg-bg-primary shadow-default"
        style={{
          left: `calc(${CENTER.x * 100}% - 28px)`,
          top: `calc(${CENTER.y * 100}% - 28px)`,
        }}
        aria-hidden="true"
      >
        <AskOpusOrbIcon size={38} />
      </div>
      {ORBITS.map((orbit) =>
        Array.from({ length: orbit.bots }, () => {
          const i = chipIndex++;
          return (
            <OrbitChip
              key={i}
              // Each bot in its own colour, as agents take one each.
              color={AGENT_COLORS[i % AGENT_COLORS.length].slug}
              chipRef={(el) => {
                chipRefs.current[i] = el;
              }}
            />
          );
        }),
      )}
      <div
        ref={bubbleRef}
        className="pointer-events-none absolute top-0 left-0 z-[6] rounded-xl bg-bg-primary px-2.5 py-1.5 opacity-0 shadow-default will-change-transform"
        aria-hidden="true"
        data-testid="agent-orbit-bubble"
      >
        <span
          ref={bubbleTextRef}
          className="block font-sans-v4 text-2xs-v4 font-medium-v4 whitespace-nowrap text-text-primary"
        />
        {/* The tail points down at the speaking bot. */}
        <span
          className="absolute -bottom-1 size-2 -translate-x-1/2 rotate-45 bg-bg-primary"
          style={{ left: "var(--tail-x, 50%)" }}
        />
      </div>
      {/* The ground fades up behind the title so it reads over the rings. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[4] h-28 bg-gradient-to-t from-bg-secondary via-bg-secondary/90 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 z-[5] px-6 pb-4">
        {children}
      </div>
    </div>
  );
};

export default AgentOrbitHeader;
